/**
 * Launcher for Desktop/Mobile 3rdParty games
 */

define(function(require, exports, module) {
    'use strict';

    var BasePlatform = require('modules/launcher/platforms/base.platform');
    var _ = require('underscore');
    var url = require('util/url.util');
    var i18n = require('i18n');
    var user = require('user');
    var language = require('util/language.util');
    var config = require('config');
    var device = require('util/device.util');

    module.exports = _.extend({}, BasePlatform, {

        /**
         * Detects formFactor
         * @return {String}
         */
        get formFactor() {
            return device.isMobilePlatform ? 'mobile' : 'desktop';
        },

        /**
         * Open 3rdParty game
         * @param {Object} game
         * @param {Number} mode
         */
        open: function(game, mode) {
            // singular changes between desktop & mobile platform
            this.provider = game.get(device.isMobilePlatform ? 'mobileGameType' : 'gameType');
            const launchMethod = device.isMobilePlatform
                ? this.getLaunchOptions().launchMethodMobile
                : this.getLaunchOptions().launchMethodDesktop;
            var isRedirectInNewWindow = launchMethod === 'redirectInNewWindow';
            var gameWindow = isRedirectInNewWindow ? url.openNewWindow() : null;

            if (launchMethod === 'wrapperInThisWindow' || launchMethod === 'wrapperInNewWindow') {
                this.launch({
                    provider: this.provider,
                    game: game,
                    mode: mode,
                    gameWindow: gameWindow,
                    urlType: this.getTypeUrl(mode)?.[0]
                });

                return;
            }

            window.Playtech.API.server.getURLTemplate({
                urlTypeList: this.getTypeUrl(mode),
                clientPlatform: device.isMobilePlatform ? 'mobile' : 'web'
            })
                .then((launchUrl) => {
                    this.successLaunch(launchUrl, gameWindow, game, mode);
                })
                .catch(() => {
                    this.failedLaunch(gameWindow, game);
                });
        },

        /**
         * Get typeUrl which depends on game mode and preview mode
         * @param {Number} mode
         * @return {Array} array with url type
         */
        getTypeUrl: function(mode) {
            const gameType = _.findWhere(config.get('gameTypes'), {name: this.provider});

            if (!gameType) {
                return [];
            }

            const isPreviewMode = Number(url.getLocationParameterByName('preview')) === 1;
            var typeUrl;

            if (isPreviewMode) {
                typeUrl = gameType[mode ? 'previewRealPlayUrl' : 'previewDemoPlayUrl'];
            }

            if (!isPreviewMode || !typeUrl) {
                typeUrl = gameType[mode ? 'realPlayUrl' : 'demoPlayUrl'];
            }

            return [typeUrl];
        },

        /**
         * Show error message and close game window if game launch failed
         */
        failedLaunch: function(gameWindow, game) {
            if (gameWindow) {
                gameWindow.close();
            }

            window.Playtech.API.popup.showPopup({
                title: game.get('name') + ' ' + i18n.t('playtech.mobile.system.launcher.gameUnavailablePopup.title'),
                content: i18n.t('playtech.mobile.system.launcher.gameUnavailablePopup.desktop-content')
            });
        },

        /**
         * Launch game after success getURLsRequest
         */
        successLaunch: async function(launchUrl, gameWindow, game, mode) {
            if (!launchUrl) {
                this.failedLaunch(gameWindow, game);
                return;
            }

            launchUrl = this.prepareLaunchUrl(launchUrl, game);

            try {
                launchUrl = await this.prepareToken(launchUrl);
            } catch (e) {
                this.failedLaunch(gameWindow, game);
                return;
            }

            this.launch({
                provider: this.provider,
                game: game,
                mode: mode,
                launchUrl: launchUrl,
                gameWindow: gameWindow
            });
        },

        /**
         * Replace [params] in launch url
         * @return {String}
         */
        prepareLaunchUrl: function(launchUrl, game) {
            return launchUrl
                .replace(/\[superdomain]/g, window.location.hostname.split('.').slice(1).join('.'))
                .replace(/\[mobilegamecode]/g, game.get('mobileGameCode') || game.get('gameCode'))
                .replace(/\[gamecode]/g, game.get('gameCode'))
                .replace('[gamename]', game.get('name'))
                .replace('[language]', language.getTwoLetterLanguage(user.get('lang')))
                .replace('[back_url]', url.origin)
                .replace('[integrationtype]', game.get('integrationType') || '')
                .replace('[livelaunchalias]', game.get('liveLaunchAlias') || '');
        },

        prepareToken: async function(launchUrl) {
            if (!launchUrl.includes('[temptoken]')) {
                return launchUrl;
            }

            const token = await window.Playtech.API.auth.getTemporaryAuthenticationToken();

            return launchUrl.replace('[temptoken]', token);
        }
    });
});
